import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Plus, Edit, Trash2, BookOpen, Users, ChevronRight } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const Classes = () => {
  const { user } = useAuth();
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const { toast } = useToast();
  const [selectedSubjectIds, setSelectedSubjectIds] = useState([]);

  const [formData, setFormData] = useState({
    name: '', numeric_name: '', description: '', sections: '',
  });

  useEffect(() => { fetchClasses(); }, [user?.school_id]);
  useEffect(() => { fetchSubjects(); }, [user?.school_id]);

  const getSelectedAcademicYearId = () => {
    const schoolId = user?.school_id;
    if (!schoolId) return null;
    return localStorage.getItem(`selected_academic_year_id_${schoolId}`);
  };

  useEffect(() => {
    const handler = () => fetchClasses();
    window.addEventListener('academicYearChanged', handler);
    return () => window.removeEventListener('academicYearChanged', handler);
  }, [user?.school_id]);

  const fetchClasses = async () => {
    try {
      const academicYearId = getSelectedAcademicYearId();
      const params = academicYearId ? { academic_year_id: academicYearId } : {};
      const response = await api.get('/classes', { params });
      setClasses(response.data.data || []);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch classes', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await api.get('/subjects');
      setSubjects(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch subjects');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const academicYearId = getSelectedAcademicYearId();
      const data = {
        ...formData,
        academic_year_id: academicYearId || undefined,
        sections: formData.sections ? formData.sections.split(',').map(s => s.trim()) : [],
        subject_ids: selectedSubjectIds,
      };
      
      if (editingClass) {
        await api.put(`/classes/${editingClass.id}`, data);
        toast({ title: 'Success', description: 'Class updated' });
      } else {
        await api.post('/classes', data);
        toast({ title: 'Success', description: 'Class created' });
      }
      setIsDialogOpen(false);
      resetForm();
      fetchClasses();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.message || 'Failed', variant: 'destructive' });
    }
  };

  const handleEdit = (cls) => {
    setEditingClass(cls);
    setFormData({
      name: cls.name || '',
      numeric_name: cls.numeric_name?.toString() || '',
      description: cls.description || '',
      sections: cls.sections?.map(s => s.name).join(', ') || '',
    });
    setSelectedSubjectIds((cls.subjects || []).map((s) => s.id));
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this class?')) {
      try {
        await api.delete(`/classes/${id}`);
        toast({ title: 'Success', description: 'Class deleted' });
        fetchClasses();
      } catch (error) {
        toast({ title: 'Error', description: 'Failed', variant: 'destructive' });
      }
    }
  };

  const resetForm = () => {
    setFormData({ name: '', numeric_name: '', description: '', sections: '' });
    setEditingClass(null);
    setSelectedSubjectIds([]);
  };

  return (
    <div className="space-y-6" data-testid="classes-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Classes</h1>
          <p className="text-slate-400">Manage classes and sections</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-class-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Class
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-800 border-slate-700 text-white">
            <DialogHeader>
              <DialogTitle>{editingClass ? 'Edit Class' : 'Add New Class'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Class Name *</Label>
                <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="bg-slate-700 border-slate-600" placeholder="e.g., Class 10" required />
              </div>
              <div>
                <Label>Numeric Value</Label>
                <Input type="number" value={formData.numeric_name} onChange={(e) => setFormData({...formData, numeric_name: e.target.value})}
                  className="bg-slate-700 border-slate-600" placeholder="e.g., 10" />
              </div>
              <div>
                <Label>Description</Label>
                <Input value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="bg-slate-700 border-slate-600" />
              </div>
              <div>
                <Label>Subjects</Label>
                {subjects.length === 0 ? (
                  <p className="text-xs text-slate-500 mt-2">No subjects found. Add subjects first.</p>
                ) : (
                  <div className="mt-2 grid grid-cols-2 gap-2 max-h-40 overflow-y-auto rounded-md border border-slate-700 p-2">
                    {subjects.map((subject) => (
                      <label key={subject.id} className="flex items-center gap-2 text-sm text-slate-300">
                        <input
                          type="checkbox"
                          className="accent-blue-500"
                          checked={selectedSubjectIds.includes(subject.id)}
                          onChange={(e) => {
                            setSelectedSubjectIds((prev) => {
                              if (e.target.checked) return [...prev, subject.id];
                              return prev.filter((id) => id !== subject.id);
                            });
                          }}
                        />
                        <span>{subject.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              {!editingClass && (
                <div>
                  <Label>Sections (comma-separated)</Label>
                  <Input value={formData.sections} onChange={(e) => setFormData({...formData, sections: e.target.value})}
                    className="bg-slate-700 border-slate-600" placeholder="e.g., A, B, C" />
                </div>
              )}
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700">
                {editingClass ? 'Update' : 'Create'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div></div>
      ) : classes.length === 0 ? (
        <Card className="bg-slate-800 border-slate-700"><CardContent className="py-12 text-center">
          <BookOpen className="w-12 h-12 text-slate-500 mx-auto mb-4" /><p className="text-slate-400">No classes found</p>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {classes.map((cls) => (
            <Card key={cls.id} className="bg-slate-800 border-slate-700 hover:border-slate-600 transition-colors">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <Link to={`/classes/${cls.id}`} className="flex items-center gap-2 min-w-0 flex-1">
                    <CardTitle className="text-white flex items-center gap-2 hover:text-blue-400 transition-colors">
                      <BookOpen className="w-5 h-5 text-blue-400 shrink-0" />
                      <span className="truncate">{cls.name}</span>
                      <ChevronRight className="w-4 h-4 text-slate-500 shrink-0" />
                    </CardTitle>
                  </Link>
                  <div className="flex gap-1 shrink-0">
                    <Button type="button" variant="ghost" size="icon" onClick={() => handleEdit(cls)} className="text-slate-400 hover:text-white h-8 w-8">
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button type="button" variant="ghost" size="icon" onClick={() => handleDelete(cls.id)} className="text-slate-400 hover:text-red-400 h-8 w-8">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <Link to={`/classes/${cls.id}`}>
                <CardContent className="cursor-pointer">
                  {cls.description && <p className="text-sm text-slate-400 mb-3">{cls.description}</p>}
                  <div className="flex flex-wrap gap-2">
                    {(cls.sections || []).map((section) => (
                      <span key={section.id} className="px-3 py-1 bg-slate-700 text-slate-300 rounded-full text-sm flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        Section {section.name}
                        {section.capacity && <span className="text-slate-500">({section.capacity})</span>}
                      </span>
                    ))}
                  </div>
                  <p className="text-xs text-slate-500 mt-3">Click to view class details</p>
                </CardContent>
              </Link>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Classes;
